import React, { useMemo, useRef } from "react";
import { IonInfiniteScroll, IonInfiniteScrollContent, IonSkeletonText } from "@ionic/react";
import { AdditionalFeature, Product } from "@core/types";
import { NewListViewProduct } from "@core/components";
import MaxWidthContainer from "../UI/MaxWidthContainer/MaxWidthContainer";
import classNames from "classnames/bind";
import styles from "./ProductsSearchResults.module.scss";

import { ProductCategories, useProductSearch } from "../../hooks/useProductSearch";
import { useHistory } from "react-router";
import CollapsibleEntityList from "../CollapsibleEntityList/CollapsibleEntityList";
import { useGetAllCategoriesQuery } from "../../providers/categories";
import { useTranslation } from "react-i18next";
import { CategoryFilters as AdditionalFilters } from "../../parhato2/src/entities";

const cx = classNames.bind(styles);

const SkeletonProducts = () => {
  return (
    <div className="grid grid-cols-2 justify-center gap-y-2.5 gap-x-2.5">
      {Array.from({ length: 10 }, (_, index) => (
        <div key={index} className="flex flex-col gap-1">
          <IonSkeletonText className="w-full h-[260px] rounded-xl" animated />
          <IonSkeletonText className="w-full h-[30px] rounded-xl" animated />
        </div>
      ))}
    </div>
  )
}

const NoProductsFound = () => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center mt-4">
      <div className="sm:text-[1.2rem] md:text-[2rem] lg:text-[3rem] font-semibold text-red-500">
        {t("No products found")}
      </div>
    </div>
  );
};

export const ProductsSearchResults = () => {
  const infiniteScrollRef = useRef<HTMLIonInfiniteScrollElement>(null);
  return (
    <MaxWidthContainer classList="!px-2.5">
      <CategoryTags />
      <ProductsList />
      <InfiniteScrollLoader ref={infiniteScrollRef} />
    </MaxWidthContainer>
  );
};

const CategoryTags = () => {
  const { t } = useTranslation();

  const history = useHistory();
  const initialVisibleCount = 3;

  const { getProductCategories, isLoading, isFetching } = useProductSearch();
  const productCategories = getProductCategories();
  const { data: { allCategories } = {} } = useGetAllCategoriesQuery();

  const handleGoToCategory = (category: ProductCategories) => {
    const hasCategory = allCategories?.some((c) => c.parent === category.categoryId);

    const linkPath = hasCategory
      ? `/categories/${category.categoryId}/subcategories`
      : `/category-detail/${category.categoryId}`;

    history.push(linkPath);
  };

  const renderCategorySkeleton = () => {
    return (
      <div className="flex flex-col items-start mb-3">
        <IonSkeletonText className="w-full h-[30px] rounded-xl" animated />
        <IonSkeletonText className="w-full h-[30px] rounded-xl" animated />
        <IonSkeletonText className="w-full h-[30px] rounded-xl" animated />
      </div>
    )
  }

  const renderCategoryTag = (cat: ProductCategories) => {
    return (
      <div onClick={() => handleGoToCategory(cat)} className="flex flex-col items-start mb-3">
        <p className="font-semibold text-sm">{cat.categoryName}</p>
        <p className="font-normal text-xs">{cat.parentName}</p>
      </div>
    );
  };

  const moreTxt = productCategories?.length
    ? `Еще ${productCategories?.length - initialVisibleCount} категории`
    : t("Show more");

  const renderShowMore = ({ showAll, setShowAll }: { showAll: boolean; setShowAll: (value: boolean) => void }) => {
    return (
      <div className="flex w-full justify-center items-center">
        <button
          onClick={() => setShowAll(!showAll)}
          className="px-3 py-2 
        text-sm text-[#454545] bg-[#F5F5F5] font-bold rounded-3xl"
        >
          {showAll ? t("Show less") : moreTxt}
        </button>
      </div>
    );
  };

  if (!productCategories?.length) return null;

  return (
    <div className={cx("flex flex-col gap-2.5 mt-2.5 mb-6")}>
      <CollapsibleEntityList
        entities={productCategories ?? []}
        renderEmpty={() => { return <div>No categories</div> }}
        renderLoading={renderCategorySkeleton}
        initialVisibleCount={initialVisibleCount}
        renderItem={(cat) => renderCategoryTag(cat)}
        isLoading={isLoading || isFetching}
        renderShowMore={renderShowMore}
        containerClassName="flex justify-center items-center "
      />
    </div>
  );
};

const ProductsList = () => {
  const { isFetching, isFetchingNextPage, getFilteredProducts, isFetched } = useProductSearch();
  const { t } = useTranslation();
  const filteredProducts = getFilteredProducts();
  const additionalFilters = useMemo(() => {
    const productsMap = new Map<string | number, AdditionalFeature>();

    const additionalFeatures = filteredProducts.length
      ? filteredProducts?.flatMap((product) => product?.additionalFeature)
      : [];


    additionalFeatures?.forEach((additionalFeature) => {
      if (additionalFeature && !productsMap.has(additionalFeature)) {
        productsMap.set(additionalFeature, { additionalFeature, count: 1 });
      }
    });

    return Array.from(productsMap.values());
  }, [filteredProducts]);


  return (
    <div className={cx("flex flex-col gap-5")}>
      {isFetching && (!isFetchingNextPage || !isFetched) ? (
        <>
          <SkeletonProducts />
        </>
      ) : (
        <div className="flex flex-col gap-2">
          {filteredProducts.length ? (
            <>
              {additionalFilters && additionalFilters.length > 0 && (
                <AdditionalFilters categoriesFilters={additionalFilters} />
              )}
              <div className="font-bold ">{t("The best")}</div>
              <div className={cx("productsSearchResult", "grid grid-cols-2 justify-center gap-y-2.5 gap-x-2.5")}>
                {filteredProducts.map((p: Product) => (
                  <NewListViewProduct key={p.id} product={p} type="newList" className="w-full h-full flex-grow" />
                ))}
              </div>
            </>
          ) : (
            <NoProductsFound />
          )}
        </div>
      )}
    </div>
  );
};

const InfiniteScrollLoader = React.forwardRef<HTMLIonInfiniteScrollElement>((_, ref) => {
  const { t } = useTranslation();
  const { hasNextPage, fetchNextPage, isFetchingNextPage } = useProductSearch();
  const isInfiniteScrollDisabled = !hasNextPage && !isFetchingNextPage;

  return (
    <IonInfiniteScroll
      ref={ref}
      threshold="100px"
      disabled={isInfiniteScrollDisabled}
      onIonInfinite={async () => {
        if (hasNextPage) {
          await fetchNextPage();
        }
        if (ref && "current" in ref) {
          ref.current?.complete();
        }
      }}
    >
      <IonInfiniteScrollContent loadingText={t("Please Wait")} loadingSpinner="crescent" />
    </IonInfiniteScroll>
  );
});
